import nuskin from './namespace.js';

/**
 *
 * For centralizing and standardizing the use of JavaScript events across the nuskin site.
 *
 *
 * IMPLEMENTING
 * Implementing events via nuskin.events is simple and straightforward.
 *
 * (1) Add the event id String as a variable in the nuskin.events.js file, in the appropriate location, such as: MY_NEW_ERROR: "myNewError".
 * (2) Publish the event through the appropriate nuskin.events wrapper function, such as nuskin.events.publishError(nuskin.events.MY_NEW_ERROR);
 * (3) Subscribe from the code location where you'd like to handle the event: nuskin.events.subscribe( nuskin.events.MY_NEW_ERROR, function(evt) {//handle event});
 *
 * Note that as of now, the wrapper functions for subscribe and publish don't do anything other than route to $.publish()
 * and $.subscribe(). But if we use the wrapper functions here we can centralize for adaptability later on.
 *
 * Also note that the functions for error event handling are designed to help standardize the transmission of data in
 * cases such as AJAX request errors.  One could just use the jQuery pub/sub functions directly, but then if we need to
 * change the event handling down the line, we'd need to handle those special cases separately.
 *
 * MESSAGE ID
 * The 'messageId' functionality,  is designed to work with the localized common strings available through the
 * StringsService global object. The 'messageId' parameter on the 'eventObject' serves to pass the value of the string id
 * that can be used to retrieve the localized string.  If a messageId is not passed with the eventObject, or passed into
 * alternative functions that take parameters instead of an object, a messageId will be extracted from the eventId
 * (stripping off the 'nuskin.events' namespace, essentially).  This default functionality serves to simplify and
 * centralize the messaging.  But to override that, simply pass in the messageId value.
 *
 * Check the docs on the event publishing functions below for examples.
 *
 */
if (!nuskin.events) {
    /**
     * @namespace nuskin.events
     * @type {{global: {STRINGS_SERVICES_LOADED: string, CONFIGURATION_SET: string, DEVELEMENT_MODE: string}, errors: {CONFIGURATION: string, STRING_SERVICE: string, ATP: string, SAP: string, CART: string, PAYMENT: string, ADDRESS: string, PRODUCT_LOOKUP: string, PRODUCT_STATUS: string, SHIPPING_SERVICE: string, ORDER_SERVICE: string, UNEXPECTED_SYSTEM: string, INVALID_SEARCH_ADDRESS: string, NUMBER_STREET_ADDRESS: string}, authentication: {LOGIN: string, HIDE_LOGIN_POPUP: string, LOGOUT: string, LOGOUT_REQUESTED: string, LOGOUT_LEGACY: string, LOGIN_REQUESTED: string, SET_LOGIN_PARAMS: string, REQUEST_FOR_LOGIN: string, REQUEST_FOR_LOGIN_MODAL: string, INVALID_LOGIN: string, VALID: string, INVALID: string, INVALID_AUTH: string, INVALID_NEW_LOGIN_PAGE: string, USER_NOT_ALLOWED: string, INVALID_EMAIL: string, PING_COMPLETE: string, AGELOCME_LOGIN: string, ERROR_COOKIE_NAME: string, EID_LOGIN_PATH: string, INVALID_SHOPPING_COUNTRY: string, STATUS: string, ERROR_USER_NOT_FOUND: string, ERROR_LOGIN_FAILED: string, ERROR_SPONSOR_NOT_FOUND: string, ERROR_SHOPPING_NOT_ALLOWED: string, ERROR_LOGGED_OUT_FOR_INACTIVITY: string, ERROR_ACCOUNT_TERMINATED: string, ERROR_TOO_MANY_LOGIN_ATTEMPTS: string, ERROR_LOGOUT_MESSAGE: string}, account: {INIT_COMPLETE: string, VOLUME_ADDED: string, EMEASIGNUP_FLAG: string}, ops: {SITE_NAME_LOADED: string, SITE_NOT_AVAILABLE: string, SITE_NAME_CHANGED: string, SITE_PRICE_TYPES_LOADED: string, MARKET_PRICE_TYPE_SETTINGS_LOADED: string, PREFERRED: string, FLAG: string}, shop: {CART_UPDATED: string, ECOM3_CART_UPDATED: string, CART_RETURNED: string, ADD_TO_CART: string, REMOVE_FROM_CART: string, OUT_OF_STOCK: string, FAVORITES_LIST_LOADED: string, ADD_TO_FAVORITES_LIST: string, ITEM_REMOVED_FROM_FAVORITES: string, ADR_OPTIONS_READY: string, PRODUCT_SEARCH_CHANGED: string, PRODUCT_SEARCH_FAILED: string, PRODUCT_SELECTED: string, PRODUCT_SEARCH_CLEAR: string, DISPLAY_CART_CHANGED: string, AGELOC_ADR_INIT: string, CHECK_USER_SAME: string, CART_ERROR_MSG: string, CART_ITEM_UPDATED: string, CART_SCAN_CARDS_CHANGED: string, INIT_CHECKOUT: string, ADR_SHIP_DAYS_CHANGED: string, CHECKOUT_FOCUS: string, ADR_STATUS_CHANGE: string, ADD_FAVORITES_LOGIN: string, NEW_GOOGLE_MAPS_SEARCH: string, STORE_FRONT_ORDER_COMPLETE: string, OFFER_PRODUCT_REMOVED: string, OFFER_PRODUCTS_CHANGED: string, STOREFRONT_LOADED: string}, social: {SHOW_SHARE_URL: string, HIDE_SHARE_URL: string, URL_SHORTNER: string, SHORTEN_URL_ERROR: string}, video: {API_READY: string, PLAYER_READY: string, PLAYER_STATE_CHANGE: string, PLAYER_ERROR: string}, mec: {PITCH_LOADED: string, SHOW_TOAST_MSG: string, OFFER_LOADED: string, OFFER_TOTAL_UPDATED: string}, on: nuskin.events.subscribe, subscribe: nuskin.events.subscribe, emit: nuskin.events.publish, publish: nuskin.events.publish, publishError: nuskin.events.publishError, publishAjaxErrorEvent: nuskin.events.publishAjaxErrorEvent, publishAjaxError: nuskin.events.publishAjaxError, getErrorEventObject: nuskin.events.getErrorEventObject, getAjaxErrorEventObject: nuskin.events.getAjaxErrorEventObject, getMessageIdFromEventId: nuskin.events.getMessageIdFromEventId}}
     */
    (function () {
        // string constants used by default, to reference both the event type and the message string

        var ns = "nuskin.events.";
        var events = {

            global: { // GLOBAL EVENTS
                STRINGS_SERVICES_LOADED: ns + "stringsServicesLoaded",
                CONFIGURATION_SET: "CONFIGURATION_SET", // legacy configuration event, so I am keeping the string value the same, just in case.
                DEVELEMENT_MODE: "DEVELEMENT_MODE" // legacy configuration event, so I am keeping the string value the same, just in case.
            },

            errors: { // GENERAL ERRORS
                CONFIGURATION: ns + "nuskinConfigurationError",
                STRING_SERVICE: ns + "stringServiceError",
                ATP: ns + "atpError",
                SAP: ns + "sapError",
                CART: ns + "cartError",
                PAYMENT: ns + "paymentError",
                ADDRESS: ns + "addressError",
                PRODUCT_LOOKUP: ns + "productLookupError",
                PRODUCT_STATUS: ns + "productStatusError",
                SHIPPING_SERVICE: ns + "shippingServiceError",
                ORDER_SERVICE: ns + "orderServiceError",
                UNEXPECTED_SYSTEM: ns + "unexpectedSystemError", // all unexpected errors go under this event - ajax, parsing,
                INVALID_SEARCH_ADDRESS: "invalidSearchAddress",
                NUMBER_STREET_ADDRESS: "numberStreetAddress"
            },

            authentication: {
                //event called when a user logs in
                LOGIN: "nuskin-account-login",
                HIDE_LOGIN_POPUP: "nuskin-account-hideLogin",
                //event called when a user logs out
                LOGOUT: "nuskin-account-logout",
                LOGOUT_REQUESTED: "nuskin-account-logoutRequested",
                LOGOUT_LEGACY: "nuskin-account-logout-legacy",
                LOGIN_REQUESTED: "nuskin-account-loginRequested",
                SET_LOGIN_PARAMS: "nuskin-account-setLoginParams",
                REQUEST_FOR_LOGIN: "nuskin-account-requestForLogin",
                REQUEST_FOR_LOGIN_MODAL: "nuskin-account-requestForLoginModal",
                INVALID_LOGIN: "invalid-login",
                //event called on valid authentication
                VALID: "nuskin-account-authentication",
                //event called when an error occurs during authentication
                INVALID: "nuskin-account-invalidAuthentication",
                INVALID_AUTH: "nuskin-account-invalidAuthentication", // This comes from static auth.
                INVALID_NEW_LOGIN_PAGE: "nuskin-account-invalidAuthentication-newLoginPage", // Use for new login page.
                USER_NOT_ALLOWED: "nuskin-account-userNotAllowed",
                INVALID_EMAIL: "invalid-email",
                //event called when a ping is complete
                PING_COMPLETE: "nuskin-account-pingComplete",
                AGELOCME_LOGIN: "agelocme-login",
                ERROR_COOKIE_NAME: "nuskin-staticauth-error",
                EID_LOGIN_PATH: "/apps/global/services/eidlogin.html",
                INVALID_SHOPPING_COUNTRY: "invalid-shopping-country",
                STATUS: "authentication-status",

                ERROR_USER_NOT_FOUND: "error-user-not-found",
                ERROR_LOGIN_FAILED: "error-login-failed",
                ERROR_SPONSOR_NOT_FOUND: "error-sponsor-not-found",
                ERROR_SHOPPING_NOT_ALLOWED: "error-shopping-not-allowed",
                ERROR_LOGGED_OUT_FOR_INACTIVITY: "error-logged-out-for-inactivity",
                ERROR_ACCOUNT_TERMINATED: "error-account-terminated",
                ERROR_TOO_MANY_LOGIN_ATTEMPTS: "error-too-many-login-attempts",
                ERROR_LOGOUT_MESSAGE: "error-logout-message"
            },

            account: {
                //event called when account initialization is complete
                INIT_COMPLETE: "nuskin-account-initializationComplete",
                //event called when volume information loaded
                VOLUME_ADDED: "nuskin-account-volumeAdded",
                EMEASIGNUP_FLAG: "emeasignup-flag"
            },

            ops: {
                SITE_NAME_LOADED: "site-name-loaded",
                SITE_NOT_AVAILABLE: "site-not-available",
                SITE_NAME_CHANGED: "site-name-changed",
                SITE_PRICE_TYPES_LOADED: "site-price-types-loaded",
                MARKET_PRICE_TYPE_SETTINGS_LOADED: "market-price-type-settings-loaded",
                //event called when a preferred member logs into the ops site
                PREFERRED: "nuskin-ops-preferred",
                //event called when the OPS passes its retail or preferred flag
                FLAG: "nuskin-ops-flag"
            },

            shop: {
                CART_UPDATED: "nuskin-shop-cart",
                ECOM3_CART_UPDATED: "ecom3-nuskin-shop-cart",
                CART_RETURNED: "nuskin-shop-cart-returned",
                ADD_TO_CART: "nuskin-shop-add-to-cart",
                REMOVE_FROM_CART: "nuskin-shop-remove-from-cart",
                OUT_OF_STOCK: "nuskin-shop-out-of-stock",
                FAVORITES_LIST_LOADED: "nuskin-favorites-list-loaded",
                ADD_TO_FAVORITES_LIST: "add-to-favorites-list-message",
                ITEM_REMOVED_FROM_FAVORITES: "item-removed-from-favorites-list",
                ADR_OPTIONS_READY: ns + "adrOptionsReady",
                PRODUCT_SEARCH_CHANGED: "product-search-changed",
                PRODUCT_SEARCH_FAILED: "product-search-failed",
                PRODUCT_SELECTED: "product-selected",
                PRODUCT_SEARCH_CLEAR: "product-search-clear",
                DISPLAY_CART_CHANGED: "display-cart-changed",
                AGELOC_ADR_INIT: "ageloc-adr-directive-init",
                CHECK_USER_SAME: "check-for-same-user",
                CART_ERROR_MSG: "add-cart-error-msg",
                CART_ITEM_UPDATED: "cart-item-updated",
                CART_SCAN_CARDS_CHANGED: "cart-scan-cards-changed",
                INIT_CHECKOUT: "init-checkout",
                ADR_SHIP_DAYS_CHANGED: "adr-ship-days-changed",
                CHECKOUT_FOCUS: "checkout-focus",
                ADR_STATUS_CHANGE: "adr_status_change",
                ADD_FAVORITES_LOGIN: "add-favorites-login",
                NEW_GOOGLE_MAPS_SEARCH: "newGoogleMapsSearch",
                STORE_FRONT_ORDER_COMPLETE: "storeFrontOrderComplete",
                OFFER_PRODUCT_REMOVED: "offerProductRemoved",
                OFFER_PRODUCTS_CHANGED: "offerVariantRemoved",
                STOREFRONT_LOADED: "storefrontLoaded",
                //COM-2043: GA & GTM new cart and checkout events
                ADD_TO_CART_NEW: "addToCart",
                REMOVE_FROM_CART_NEW: "removeFromCart",
                CART_CHECKOUT: "checkout",
                CART_CHECKOUT_PURCHASE: "checkout-purchase",
                //COM-2734/2736: Product Impression GA event
                PRODUCT_IMPRESSION: "productImpression",
                PRODUCT_IMPRESSION_CLICK: "productClick",
                PRODUCT_IMPRESSION_DETAIL: "productDetailImpression"
            },

            social: {
                SHOW_SHARE_URL: ns + "showShareUrl",
                HIDE_SHARE_URL: ns + "hideShareUrl",
                URL_SHORTNER: ns + "UrlShortner",
                SHORTEN_URL_ERROR: ns + "shortenUrlError"
            },

            video: {
                API_READY: ns + "videoApiReady",
                PLAYER_READY: ns + "videoPlayerReady",
                PLAYER_STATE_CHANGE: ns + "videoPlayerStateChange",
                PLAYER_ERROR: ns + "videoPlayerError"
            },

            mec: {
                PITCH_LOADED: "pitchLoaded",
                SHOW_TOAST_MSG: "showToastMsg",
                OFFER_LOADED: "offerLoaded",
                OFFER_TOTAL_UPDATED: "offerTotalUpdated"
            },

            salesevent: {
                PRE_EVENT: "pre-event",
                IN_EVENT: "in-event",
                POST_EVENT: "post-event"
            },

            // PUBLIC METHODS
            on: subscribe,
            subscribe: subscribe,
            emit: publish,
            publish: publish,
            publishError: publishError,
            publishAjaxErrorEvent: publishAjaxErrorEvent,
            publishAjaxError: publishAjaxError,
            getErrorEventObject: getErrorEventObject,
            getAjaxErrorEventObject: getAjaxErrorEventObject,
            getMessageIdFromEventId: getMessageIdFromEventId
        };

        // jQuery pub/sub wrappers

        /**
         * Just a wrapper over the jQuery subscribe to centralize and possibly extend nuskin event handling in general.
         *
         * @param {string} eventId
         * @param {function} eventCallback
         * @memberof nuskin.events
         */
        function subscribe(eventId, eventCallback) {
            $.subscribe(eventId, eventCallback);
        }


        /**
         * Just a wrapper over the jQuery publish to centralize and possibly extend nuskin event handling in general.
         *
         * @param {string} eventId
         * @param {object} [eventObject]
         * @memberof nuskin.events
         */
        function publish(eventId, eventObject) {
            $.publish(eventId, eventObject);
        }


        /**
         * Wrapper for centralizing and simplifying the general ERROR event publishing, via an object.
         *
         * @param {string}  eventId     - The string constant id for the event.  If a message id is not passed, the eventId is also used for extracting a message id used retrieving the localized message from common strings. If the Event id is passed, a standardized messageId will be derived therefrom.
         * @param {{messageId: string, messageString: string}}  [eventObject] - optional event object - will automatically extract the messageId from the event Id if none is passed.
         * @memberof nuskin.events
         * @private
         */
        function publishError(eventId, eventObject) {
            $.publish(eventId, events.getErrorEventObject(eventObject.messageId ? eventObject.messageId : eventId, eventObject.messageString));
        }


        /**
         * Wrapper for centralizing, standardizing, and simplifying the AJAX ERROR event publishing.
         *
         * @example <caption>Example usage</caption>
         * // Note that passing null for the messageId param makes it default to the value of 'eventId'
         * nuskin.events.publishAjaxErrorEvent(nuskin.events.ERROR_GENERIC, url, jqXHR, null, errorString);
         *
         * @param {string}  eventId - nuskin.events string constant used to reference both the error type and the message string.
         * @param {string}  url - URL of the JSON response
         * @param {object}  response - the general xhr response object, containing both a 'status' and a 'statusText' parameter.
         * @param {string}  [messageId] - reference for the string ID used in CQ common strings for use with the StringServices object. Defaults to the Event string minus the prefix namespace, "nuskin.events."
         * @param {string}  [messageString] - optional message string if an messageId lookup isn't ideal.
         * @memberof nuskin.events
         *
         */
        function publishAjaxErrorEvent(eventId, url, response, messageId, messageString) {
            $.publish(eventId, events.getAjaxErrorEventObject(messageId ? messageId : eventId, url, response, messageString));
        }


        /**
         * Wrapper for centralizing and simplifying the ajax error event publishing, via an object.
         *
         * @example <caption>Example usage</caption>
         * var eventObj = {
         *     messageId:"myCustomMessageId",
         *     url:"someurl.com",
         *     response: httpResponse
         * }
         * nuskin.events.publishAjaxError(evtId, eventObj);
         *
         * @param {string}  eventId     - The string constant id for the event.  If a messageId is not sent, the eventId is also used for retrieving the localized message from common strings.
         * @param {{url: string, response: *, messageId: string, messageString: string}}  eventObject
         * @memberof nuskin.events
         */
        function publishAjaxError(eventId, eventObject) {
            $.publish(eventId, events.getAjaxErrorEventObject(eventObject.messageId ? eventObject.messageId : eventId, eventObject.url, eventObject.response, eventObject.messageString));
        }


        /**
         * Factory function for standardizing and centralizing the creation of the GENERIC error event objects
         *
         * @param {string}  id - The id used for retrieving the localized message from common strings. If the Event id is passed, a standardized messageId will be derived therefrom.
         * @param {string}  [messageString]
         *
         * @returns {{messageId: string, messageString: string}} - messageId is for use with the localized common strings
         * @memberof nuskin.events
         */
        function getErrorEventObject(id, messageString) {
            return {
                messageId: events.getMessageIdFromEventId(id),
                messageString: messageString
            };
        }


        /**
         * Factory function for standardizing and centralizing the creation of the AJAX error event objects
         *
         * @param {string}  id - The id used for retrieving the localized message from common strings. If the Event id is passed, a standardized messageId will be derived therefrom.
         * @param {string}  url
         * @param {Object}  response - the xhr response
         * @param {string}  [messageString]
         *
         * @returns {{url: string, response: *, messageId: string, messageString: string}}
         * @memberof nuskin.events
         */
        function getAjaxErrorEventObject(id, url, response, messageString) {
            return _.extend(getErrorEventObject(id, messageString), {
                url: url,
                response: response
            });
        }


        /**
         * Standardizes and centralizes the default practice of repurposing or coupling event and message ids.
         *
         * @param {string}      id
         *
         * @returns {string}
         * @memberof nuskin.events
         */
        function getMessageIdFromEventId(id) {
            if (id == undefined) {
                throw new Error("ERROR in nuskin.events.js.getMessageIdFromEventId():: id is undefined.");
            }
            var index = id.indexOf(ns);
            if (index > -1) {
                id = id.substring(id.indexOf(ns) + ns.length);
            }
            return id;
        }

        nuskin.events = events;

    })();
}

export default nuskin.events;



//////////////////
// WEBPACK FOOTER
// /Users/geoffsagers/dev/webcomponents/js/packages/common-account/node_modules/@nuskin/ns-util/src/event.js
// module id = 21
// module chunks = 0